/**
 * RocketLawyer draft-preview retention rule (RAC-10332).
 *
 * RocketLawyer inlines the whole document, as HTML, in the page envelopes we
 * already fetch — `patchRemotePage()` asks for it on every PATCH, and the
 * server decodes it onto `page.preview_html`. It is NOT guaranteed to be there:
 * the bare GET that opens (or resumes) an interview does not request one, so a
 * first page load can legitimately arrive with nothing.
 *
 * That absence is the whole reason this module exists. A missing preview means
 * "nothing new to show", never "the document is gone" — the wizard keeps
 * displaying the last preview it received, and under no circumstance issues an
 * extra RocketLawyer call to go and fetch one. Rendering the payload that
 * already rides along is what keeps this feature free; a fetch-on-miss would
 * quietly turn every wizard page into another billable document request.
 *
 * `changed` is deliberately part of the return value rather than something the
 * caller infers. Re-writing an iframe's srcdoc with a byte-identical document
 * is still a navigation: it reparses ~55KB and throws the reader back to the
 * top of the lease. Knowing the HTML is unchanged lets the caller skip the
 * write entirely, which is the only way to preserve scroll perfectly. Adjacent
 * pages usually DO change the document — that is the point of the feature — so
 * this is the cheap guard for the pages that don't (a page the landlord walked
 * past without answering, or a Back), not a common case.
 *
 * Pure + framework-free on purpose: no Vue imports, so it is unit-testable in
 * isolation against the captured page fixtures.
 */

/**
 * @param {?string} currentHtml  the preview currently mounted, or null
 * @param {?object} page         a normalized wizard page from the server
 * @returns {{html: ?string, changed: boolean}}
 */
export function resolvePreview(currentHtml, page) {
    const retain = { html: currentHtml ?? null, changed: false }

    if (!page || typeof page !== 'object') {
        return retain
    }

    const incoming = page.preview_html

    if (typeof incoming !== 'string' || incoming === '') {
        return retain
    }

    if (incoming === currentHtml) {
        return retain
    }

    return { html: incoming, changed: true }
}

/**
 * The draft watermark (RAC-10332, per Rich: text, reading "RentRisk Draft").
 *
 * Applied here rather than server-side so `preview_html` stays byte-identical
 * to what RocketLawyer generated — the watermark is a display concern, and
 * resolvePreview()'s change detection above keeps comparing RL's own bytes
 * rather than our decorated copy.
 *
 * It has to be pure CSS. The iframe that renders this runs with
 * `sandbox="allow-same-origin"` and deliberately WITHOUT allow-scripts, so any
 * JS we injected here would silently never run.
 *
 * A tiled background rather than a fixed overlay: the tile repeats down the
 * whole document, so the mark is present wherever the reader has scrolled and
 * on every page of a print, instead of sitting once on the glass.
 */
const WATERMARK_TEXT = 'RentRisk Draft'

// Built as a data URI rather than markup injected into the document, so it
// cannot interact with RocketLawyer's own DOM or selectors. WATERMARK_TEXT is a
// module constant, never user input, so there is no injection surface;
// encodeURIComponent still escapes the quotes and '#' that would otherwise
// terminate the url() early.
const WATERMARK_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="340" height="220">'
    + '<text x="170" y="110" text-anchor="middle" transform="rotate(-30 170 110)"'
    + ' font-family="Helvetica,Arial,sans-serif" font-size="26" font-weight="600"'
    + ` fill="#111827" fill-opacity="0.07">${WATERMARK_TEXT}</text>`
    + '</svg>'

// Invariant, so it is built once at import: applyWatermark() must return the
// same bytes for the same document every time. The iframe reloads on any srcdoc
// change, and a reload resets the reader's scroll position.
//
// print-color-adjust is load-bearing, not a nicety: browsers drop background
// graphics when printing by default, which would strip the watermark in exactly
// the case it exists for — someone printing the draft and walking off with it.
const WATERMARK_STYLE = '<style data-rentrisk-watermark>html{'
    + `background-image:url("data:image/svg+xml;utf8,${encodeURIComponent(WATERMARK_SVG)}");`
    + 'background-repeat:repeat;'
    + '-webkit-print-color-adjust:exact;print-color-adjust:exact;'
    + '}</style>'

/**
 * Decorate RocketLawyer's document HTML with the draft watermark.
 *
 * @param {?string} html  document HTML as RocketLawyer sent it, or null
 * @returns {?string}     the same document with the watermark, or null unchanged
 */
export function applyWatermark(html) {
    if (typeof html !== 'string' || html === '') {
        return null
    }

    // RL closes with a single </html>. Slotting the style in ahead of it keeps
    // our rule last, so it wins against their <style> block without needing
    // !important. If the shape ever changes, appending still works — a trailing
    // style element is hoisted into the document by the parser.
    const closing = html.lastIndexOf('</html>')

    return closing === -1
        ? html + WATERMARK_STYLE
        : html.slice(0, closing) + WATERMARK_STYLE + html.slice(closing)
}
